import { useState, useMemo } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { Etapa, ConfiguracaoHierarquiaEtapa } from '../../types';

interface ConfiguracaoHierarquiaProps {
  etapa: Etapa;
  todasEtapas: Etapa[];
  automacoes?: any[];
  cards?: any[];
  onSave: (config: ConfiguracaoHierarquiaEtapa) => void;
  onClose: () => void;
}

const ConfiguracaoHierarquia = ({
  etapa,
  todasEtapas,
  automacoes = [],
  cards = [],
  onSave,
  onClose
}: ConfiguracaoHierarquiaProps) => {
  const [config, setConfig] = useState<ConfiguracaoHierarquiaEtapa>(
    etapa.configuracaoHierarquia || {
      permitirCardsPai: false,
      regrasTransformacao: {
        condicoes: [],
        automacoesBloqueadoras: [],
        etapasPermitidas: []
      },
      regraAgrupamento: {
        aceitaFilhos: false,
        condicoesFilho: [],
        automacoesBloqueadorasFilho: [],
        etapasProibidasParaFilhos: [],
        limiteFilhos: undefined
      },
      visualCardPai: {
        corDestaque: '#9333ea',
        icone: '🎴',
        mostrarContagemFilhos: true
      }
    }
  );

  // Filtrar automações que podem afetar cards filhos
  // Mostra TODAS as automações do fluxo porque cards de qualquer etapa podem virar filhos
  const automacoesRelevantes = useMemo(() => {
    console.log('🔍 [ConfiguracaoHierarquia] Automações recebidas:', automacoes);
    console.log('🔍 [ConfiguracaoHierarquia] Etapa atual:', { id: etapa.id, nome: etapa.nome });
    
    if (!automacoes || automacoes.length === 0) {
      console.log('⚠️ [ConfiguracaoHierarquia] Nenhuma automação disponível');
      return [];
    }
    
    // Retornar TODAS as automações (ativas ou indefinidas)
    // Porque cards de qualquer etapa podem se tornar filhos
    const filtradas = automacoes.filter(automacao => {
      console.log('🔎 Verificando automação:', {
        nome: automacao.nome,
        ativa: automacao.ativa,
        tipoAtiva: typeof automacao.ativa,
        etapaOrigem: automacao.etapaOrigem,
        flowId: automacao.flowId
      });
      
      // Considerar ativa se o campo for true OU se não estiver definido (undefined)
      // Apenas rejeitar se explicitamente false
      return automacao.ativa !== false;
    });
    
    console.log('✅ [ConfiguracaoHierarquia] Automações encontradas:', filtradas.length);
    console.log('📋 Lista de automações:', filtradas.map(a => ({ nome: a.nome, etapaOrigem: a.etapaOrigem, ativa: a.ativa })));
    return filtradas;
  }, [automacoes, etapa.id]);

  const handleSave = () => {
    onSave(config);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[75vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Configuração de Hierarquia</h2>
            <p className="text-[10px] text-gray-500">
              {etapa.nome} · {automacoesRelevantes.length} automações ativas
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-200 rounded transition-colors"
          >
            <XMarkIcon className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          
          {/* Seção 1: Permitir Cards Pai */}
          <div className="bg-gray-50 rounded-md p-3 border border-gray-200">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                id="permitirCardsPai"
                checked={config.permitirCardsPai}
                onChange={(e) => setConfig({ ...config, permitirCardsPai: e.target.checked })}
                className="mt-0.5 w-3.5 h-3.5 text-indigo-600 rounded focus:ring-indigo-500"
              />
              <div className="flex-1">
                <span className="text-xs font-semibold text-gray-900">
                  Permitir transformar cards em "Cards Pai" nesta etapa
                </span>
                <p className="text-[10px] text-gray-500 mt-0.5">
                  Cards poderão ser transformados em cards pai, agrupando outros cards como filhos
                </p>
              </div>
            </label>
          </div>

          {/* Seção 2: Regras de Transformação */}
          {config.permitirCardsPai && (
            <div className="rounded-md p-3 border border-gray-200">
              <h3 className="text-xs font-semibold text-gray-900 mb-2">Regras para Transformação</h3>
              <p className="text-[10px] text-gray-500 mb-2">Etapas onde cards podem virar pai (vazio = todas)</p>
              <div className="grid grid-cols-2 gap-1">
                {todasEtapas.map(e => (
                  <label key={e.id} className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded hover:bg-gray-100 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.regrasTransformacao?.etapasPermitidas?.includes(e.id) || false}
                      onChange={(evt) => {
                        const etapasPermitidas = config.regrasTransformacao?.etapasPermitidas || [];
                        const novasEtapas = evt.target.checked
                          ? [...etapasPermitidas, e.id]
                          : etapasPermitidas.filter(id => id !== e.id);
                        setConfig({
                          ...config,
                          regrasTransformacao: {
                            ...config.regrasTransformacao,
                            etapasPermitidas: novasEtapas
                          }
                        });
                      }}
                      className="w-3.5 h-3.5 text-indigo-600 rounded"
                    />
                    <span className="text-xs text-gray-700">{e.nome}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Seção 3: Aceitar Filhos */}
          {config.permitirCardsPai && (
            <div className="bg-gray-50 rounded-md p-3 border border-gray-200">
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  id="aceitaFilhos"
                  checked={config.regraAgrupamento?.aceitaFilhos || false}
                  onChange={(e) => setConfig({
                    ...config,
                    regraAgrupamento: {
                      ...config.regraAgrupamento,
                      aceitaFilhos: e.target.checked
                    }
                  })}
                  className="mt-0.5 w-3.5 h-3.5 text-indigo-600 rounded focus:ring-indigo-500"
                />
                <div className="flex-1">
                  <span className="text-xs font-semibold text-gray-900">
                    Cards pai desta etapa podem aceitar filhos
                  </span>
                  <p className="text-[10px] text-gray-500 mt-0.5">
                    Outros cards poderão ser arrastados para dentro dos cards pai desta etapa
                  </p>
                </div>
              </label>
            </div>
          )}

          {/* Seção 4: Regras de Agrupamento de Filhos */}
          {config.permitirCardsPai && config.regraAgrupamento?.aceitaFilhos && (
            <div className="rounded-md p-3 border border-gray-200">
              <h3 className="text-xs font-semibold text-gray-900 mb-2">Regras para Aceitar Filhos</h3>
              
              <div className="space-y-3">
                {/* Limite de Filhos */}
                <div>
                  <label className="block text-[10px] font-medium text-gray-600 mb-1">
                    Limite máximo de filhos (vazio = ilimitado)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={config.regraAgrupamento?.limiteFilhos || ''}
                    onChange={(e) => setConfig({
                      ...config,
                      regraAgrupamento: {
                        ...config.regraAgrupamento,
                        aceitaFilhos: config.regraAgrupamento?.aceitaFilhos || false,
                        limiteFilhos: e.target.value ? parseInt(e.target.value) : undefined
                      }
                    })}
                    placeholder="Ex: 5"
                    className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                {/* Etapas Proibidas para Filhos */}
                <div>
                  <label className="block text-[10px] font-medium text-gray-600 mb-1">
                    Etapas cujos cards NÃO podem virar filhos
                  </label>
                  <div className="grid grid-cols-2 gap-1">
                    {todasEtapas.map(e => (
                      <label key={e.id} className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded hover:bg-gray-100 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={config.regraAgrupamento?.etapasProibidasParaFilhos?.includes(e.id) || false}
                          onChange={(evt) => {
                            const etapasProibidas = config.regraAgrupamento?.etapasProibidasParaFilhos || [];
                            const novasEtapas = evt.target.checked
                              ? [...etapasProibidas, e.id]
                              : etapasProibidas.filter(id => id !== e.id);
                            setConfig({
                              ...config,
                              regraAgrupamento: {
                                ...config.regraAgrupamento,
                                aceitaFilhos: config.regraAgrupamento?.aceitaFilhos || false,
                                etapasProibidasParaFilhos: novasEtapas
                              }
                            });
                          }}
                          className="w-3.5 h-3.5 text-red-500 rounded"
                        />
                        <span className="text-xs text-gray-700">{e.nome}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Automações que Afetam Cards Filhos */}
                {automacoesRelevantes.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <h4 className="text-xs font-semibold text-gray-900 mb-1">
                      Automações Ativas ({automacoesRelevantes.length})
                    </h4>
                    <p className="text-[10px] text-gray-500 mb-2">
                      Escolha quais automações os cards filhos devem ou não executar
                    </p>
                    
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {automacoesRelevantes.map((automacao) => {
                        const automacaoBloqueada = config.regraAgrupamento?.automacoesBloqueadorasFilho?.includes(automacao._id || automacao.id) || false;
                        
                        // Determinar ícone e cor por tipo de ação
                        const acaoPrincipal = automacao.acoes?.[0];
                        let icone = '⚙️';
                        let corBorda = 'border-gray-300';
                        let corFundo = 'bg-gray-50';
                        
                        if (acaoPrincipal?.tipo === 'mover_card') {
                          icone = '➡️';
                          corBorda = 'border-blue-300';
                          corFundo = 'bg-blue-50';
                        } else if (acaoPrincipal?.tipo === 'enviar_email') {
                          icone = '📧';
                          corBorda = 'border-green-300';
                          corFundo = 'bg-green-50';
                        } else if (acaoPrincipal?.tipo === 'enviar_whatsapp') {
                          icone = '📱';
                          corBorda = 'border-emerald-300';
                          corFundo = 'bg-emerald-50';
                      } else if (acaoPrincipal?.tipo === 'exibir_ocultar_campo') {
                        icone = '👁️';
                        corBorda = 'border-purple-300';
                        corFundo = 'bg-purple-50';
                      }
                      
                      // Encontrar nome da etapa de origem
                      // CORREÇÃO: Usar o nome correto do campo MongoDB
                      const etapaOrigemId = automacao.etapaOrigemId;
                      console.log('🔍 Buscando etapa origem:', {
                        automacaoNome: automacao.nome,
                        etapaOrigemId: automacao.etapaOrigemId,
                        flowId: automacao.flowId,
                        tipoEtapaOrigem: typeof etapaOrigemId,
                        todasEtapasIds: todasEtapas.map(e => ({ id: e.id, nome: e.nome, tipo: typeof e.id }))
                      });
                      
                      const etapaOrigem = todasEtapas.find(e => String(e.id) === String(etapaOrigemId));
                      const nomeOrigem = etapaOrigem?.nome || (etapaOrigemId ? 'Desconhecida' : 'Todas as etapas');
                                              return (
                          <div
                            key={automacao._id || automacao.id}
                            className={`border border-gray-200 bg-white rounded-md px-3 py-2 transition-all ${
                              automacaoBloqueada ? 'opacity-40' : ''
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <span className="text-xs flex-shrink-0">{icone}</span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium text-gray-900 truncate">{automacao.nome}</p>
                                  <p className="text-[10px] text-gray-400">
                                    {nomeOrigem}
                                    {acaoPrincipal?.tipo === 'mover_card' && (
                                      <> · Move para {todasEtapas.find(e => String(e.id) === String(acaoPrincipal.etapaDestino))?.nome || 'outra etapa'}</>
                                    )}
                                    {acaoPrincipal?.tipo === 'enviar_email' && ' · E-mail'}
                                    {acaoPrincipal?.tipo === 'enviar_whatsapp' && ' · WhatsApp'}
                                    {acaoPrincipal?.tipo === 'exibir_ocultar_campo' && ' · Campo'}
                                  </p>
                                </div>
                              </div>
                              <label className="flex items-center gap-1.5 cursor-pointer flex-shrink-0">
                                <input
                                  type="checkbox"
                                  checked={automacaoBloqueada}
                                  onChange={(e) => {
                                    const automacoesBloquedas = config.regraAgrupamento?.automacoesBloqueadorasFilho || [];
                                    const automacaoId = automacao._id || automacao.id;
                                    const novasAutomacoes = e.target.checked
                                      ? [...automacoesBloquedas, automacaoId]
                                      : automacoesBloquedas.filter(id => id !== automacaoId);
                                    
                                    setConfig({
                                      ...config,
                                      regraAgrupamento: {
                                        ...config.regraAgrupamento,
                                        aceitaFilhos: config.regraAgrupamento?.aceitaFilhos || false,
                                        automacoesBloqueadorasFilho: novasAutomacoes
                                      }
                                    });
                                  }}
                                  className="w-3.5 h-3.5 text-red-500 rounded focus:ring-red-500"
                                />
                                <span className="text-[10px] text-gray-500">Bloquear</span>
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    <p className="mt-2 text-[10px] text-gray-400 italic">
                      Você autoriza cards individualmente ao agrupá-los. Cada card pode ser exceção.
                    </p>

                    {/* Lista de Cards Autorizados como Exceção */}
                    {config.regraAgrupamento?.cardsExcecaoAutomacao && config.regraAgrupamento.cardsExcecaoAutomacao.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-100">
                        <p className="text-[10px] font-medium text-gray-600 mb-1">
                          Cards exceção ({config.regraAgrupamento.cardsExcecaoAutomacao.length})
                        </p>
                        <div className="space-y-0.5">
                          {config.regraAgrupamento.cardsExcecaoAutomacao.map((cardId) => {
                            const card = cards?.find(c => String(c.id) === String(cardId));
                            return (
                              <div key={cardId} className="flex items-center justify-between bg-gray-50 rounded px-2 py-1">
                                <span className="text-[10px] text-gray-600 truncate">
                                  {card?.titulo || `ID: ${cardId}`}
                                </span>
                                <button
                                  onClick={() => {
                                    const novaLista = config.regraAgrupamento?.cardsExcecaoAutomacao?.filter(id => id !== cardId) || [];
                                    setConfig({
                                      ...config,
                                      regraAgrupamento: {
                                        ...config.regraAgrupamento,
                                        aceitaFilhos: config.regraAgrupamento?.aceitaFilhos || false,
                                        cardsExcecaoAutomacao: novaLista
                                      }
                                    });
                                  }}
                                  className="text-red-400 hover:text-red-600 text-xs ml-2"
                                  title="Remover"
                                >
                                  ✕
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Seção 5: Visual do Card Pai */}
          {config.permitirCardsPai && (
            <div className="rounded-md p-3 border border-gray-200">
              <h3 className="text-xs font-semibold text-gray-900 mb-2">Aparência do Card Pai</h3>
              
              <div className="space-y-2">
                <div>
                  <label className="block text-[10px] font-medium text-gray-600 mb-1">Ícone</label>
                  <input
                    type="text"
                    value={config.visualCardPai?.icone || '🎴'}
                    onChange={(e) => setConfig({
                      ...config,
                      visualCardPai: {
                        ...config.visualCardPai,
                        icone: e.target.value
                      }
                    })}
                    placeholder="🎴"
                    className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.visualCardPai?.mostrarContagemFilhos !== false}
                    onChange={(e) => setConfig({
                      ...config,
                      visualCardPai: {
                        ...config.visualCardPai,
                        mostrarContagemFilhos: e.target.checked
                      }
                    })}
                    className="w-3.5 h-3.5 text-indigo-600 rounded"
                  />
                  <span className="text-xs text-gray-700">Mostrar badge com contagem de filhos</span>
                </label>
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-1.5 text-xs bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors font-medium"
          >
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfiguracaoHierarquia;
